import React from 'react';
import { useSearchParams } from 'react-router-dom';

import CaretIcon from '@hero/ui-kit/icons/utility/CaretIcon';
import ClickableOpacity from '@hero/ui-kit/inputs/ClickableOpacity';
import { SortDir, nextSortDir } from '../../utils/tableSort';
import * as Style from './style.module.scss';

type Props = {
    label: string;
    /** Backend field this header sorts by; each list types its own set in its `columns.ts`. */
    field: string;
    /** Direction a first click opens on ('DESC' for dates, 'ASC' for names). */
    defaultDir?: SortDir;
    /** Match the column's own alignment so the caret sits with a centered header. */
    centered?: boolean;
};

const SortBy: React.FC<Props> = ({ label, field, defaultDir = 'DESC', centered }) => {
    const [searchParams, setSearchParams] = useSearchParams();

    const isActiveSort = searchParams.get('sort_by') === field;
    const isAscending = isActiveSort && searchParams.get('sort_dir') === 'ASC';

    const handleClick = React.useCallback(() => {
        setSearchParams((params) => {
            // Read the active sort off `params` rather than closing over it, so the handler cannot
            // act on a stale direction when several headers re-render from one navigation. Must be
            // read before `sort_by` is overwritten below.
            const dir = nextSortDir(
                params.get('sort_by') === field,
                params.get('sort_dir'),
                defaultDir
            );
            params.set('sort_by', field);
            params.set('sort_dir', dir);
            // A reordered list makes the current page meaningless — send the user back to page one.
            params.delete('offset');
            return params;
        });
    }, [setSearchParams, field, defaultDir]);

    return (
        <ClickableOpacity
            alt={`Sort by ${label}${isActiveSort ? ` (${isAscending ? 'ascending' : 'descending'})` : ''}`}
            onClick={handleClick}
            className={`${Style.sortBy} ${centered ? Style.centered : ''}`}
        >
            <strong>{label}</strong>
            <CaretIcon
                direction={isAscending ? 'up' : 'down'}
                size="small"
                strokeWidth={3}
                // Muted until active, so every sortable header advertises itself without three
                // carets competing with the one that is actually ordering the list.
                disabled={!isActiveSort}
            />
        </ClickableOpacity>
    );
};

export default SortBy;
